/**
 * Persistent master-mute button — fixed at the top-right of the viewport,
 * always visible across title screen, scenes, modals, and end overlays.
 *
 * Toggling this button mutes/unmutes ALL audio (BGM + SFX) via
 * AudioEngine.toggleMasterMute(). Visual state stays in sync with the
 * engine via the AudioEngine subscribe() listener, so it also updates
 * if the master state is changed elsewhere (e.g. by tests or future UI).
 */

import { AudioEngine } from '../engine/audio';

const BUTTON_ID = 'master-mute-btn';

interface MountedButton {
  el: HTMLButtonElement;
  unsubscribe: () => void;
}

/**
 * Mount the master mute button on the document body. Idempotent — calling
 * it twice returns the existing button instead of creating a duplicate.
 */
export function mountMasterMuteButton(): MountedButton {
  const existing = document.getElementById(BUTTON_ID) as HTMLButtonElement | null;
  if (existing) {
    // Already mounted; return a no-op unsubscribe.
    return { el: existing, unsubscribe: () => undefined };
  }

  const audio = AudioEngine.getInstance();

  const btn = document.createElement('button');
  btn.id = BUTTON_ID;
  btn.type = 'button';
  btn.className = 'master-mute-btn';

  applyVisual(btn, audio.masterMuted);

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    // Make sure context is unlocked on first user gesture so subsequent
    // un-mute correctly resumes audio.
    void audio.ensureContext();
    const nowMuted = audio.toggleMasterMute();
    // Best-effort confirmation click — only audible when un-muting.
    if (!nowMuted) {
      void audio.playSfx('./assets/audio/sfx/ui-click.mp3');
    }
  });

  // Keep visuals in sync with engine state (covers programmatic changes too).
  const unsubscribe = audio.subscribe((settings) => {
    applyVisual(btn, settings.masterMuted);
  });

  document.body.appendChild(btn);

  return { el: btn, unsubscribe };
}

function applyVisual(btn: HTMLButtonElement, muted: boolean): void {
  btn.textContent = muted ? '🔇' : '🔊';
  btn.setAttribute('aria-pressed', String(muted));
  btn.setAttribute(
    'aria-label',
    muted ? 'Unmute all sound' : 'Mute all sound',
  );
  btn.title = muted ? 'Unmute all sound' : 'Mute all sound';
  btn.dataset['muted'] = muted ? 'true' : 'false';
}
